import { Box, Button, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { type TeamQuery } from 'generated/resources/graphql'

import Popover, {
  type PopoverMenuTouchableOptionProps as Option,
} from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useRemoveMember from 'modules/teams/components/Detail/hooks/useRemoveMember'
import app from 'modules/core/store/models/app'
import useDemoteMember from 'modules/teams/components/Detail/hooks/useMemberDemote'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  membership: TeamMembership
  canManageTeams: boolean
  hasDevJobCatalogTree: boolean
}

const Leader = ({ membership, canManageTeams, hasDevJobCatalogTree }: Props) => {
  const { removeMember, loading: removeLoading } = useRemoveMember()
  const { demoteMember, loading: demoteLoading } = useDemoteMember()
  const employee = membership.employee
  const jobCatalogLevel = employee.referenceContract?.jobCatalogLevel
  const jobTitle = employee.referenceContract?.jobTitle
  const role = jobCatalogLevel?.role

  // Compute the job display - prefer jobTitle, then role + level
  let job = ''
  if (jobTitle) {
    job = jobTitle
  } else if (role && !role.archived) {
    const levelName = jobCatalogLevel?.isDefault ? '' : jobCatalogLevel?.name
    job = role.name && levelName ? `${role.name}, ${levelName}` : role.name
  }
  const { navigation } = useNavigation()

  const onClickMemberDemote = async () => {
    const result = await demoteMember({ id: membership.id })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const onClickMemberRemove = async () => {
    const result = await removeMember({ id: membership.id })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const options: Option[] = [
    {
      title: i18n.t('teams.change_to_member'),
      onClick: onClickMemberDemote,
    },
    {
      title: i18n.t('teams.leaders.remove_title'),
      onClick: onClickMemberRemove,
      danger: true,
    },
  ]

  return (
    <Box
      padding="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      style={{ maxWidth: '100%' }}
      borderRadius={{
        all: 'abs008',
      }}
    >
      {removeLoading || demoteLoading ? (
        <Skeleton width="full" height="s40" />
      ) : (
        <Box width="full" gap="s16" flexDirection="row" justifyContent="spaceBetween">
          <Box justifyContent="flexStart" flexDirection="row" alignItems="center">
            <UserAvatar
              user={{
                fullName: `${employee.firstName} ${employee.lastName}`,
                avatar: employee.avatar?.url ?? '',
              }}
              size="s40"
            />
            <Box paddingLeft="s24" alignItems="flexStart">
              <Text.Normal weight="medium" color="grey1000">
                {getEmployeeDisplayName({
                  fullName: `${employee.firstName} ${employee.lastName}`,
                  lastName: employee.lastName,
                  preferredName: employee.preferredName ?? undefined,
                })}
              </Text.Normal>
              {!hasDevJobCatalogTree && (
                <Text.Normal size="200" color="grey800" weight="medium">
                  {job}
                </Text.Normal>
              )}
            </Box>
          </Box>
          <Box alignItems="center" justifyContent="flexEnd" flexDirection="row" gap="s12">
            {canManageTeams && (
              <Popover type="list">
                <Popover.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </Popover.Trigger>
                <Popover.Content side="bottom" align="center">
                  {options.map(({ title, description, onClick, danger }, index) => (
                    <Popover.TouchableOption
                      key={index}
                      danger={danger}
                      title={title}
                      onClick={onClick}
                      description={description}
                    />
                  ))}
                </Popover.Content>
              </Popover>
            )}
            <Button.Router
              to={navigation.employees.id.fullPath({ id: employee.id })}
              type="secondary"
              iconName="arrow-right"
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default Leader
